import { administerLoanStatement } from 'APIs/loan.statement/loanstatement';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { Label } from 'Constants/styles';
import { FormEvent, MouseEvent, ReactElement, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatAmount, minorCurrencyUnits } from 'utils/currency';
import getErrorMessage from 'utils/getErrorMessage';

import { FormField, FormRow, TextInput } from './styles';

type Props = {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
};

const LoanStatementManualPayment = ({ showModal, setShowModal }: Props): ReactElement => {
  const { apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [loanStatementId, setLoanStatementId] = useState<string>('');
  const [newEntryAmount, setNewEntryAmount] = useState<string>('');
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');

  const handleAddEntry = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setModalErrorMsg('');

      try {
        const entryAmount = Number(newEntryAmount.replace(/[^0-9.-]/g, ''));
        if (loanStatementId && entryAmount !== 0) {
          const confirmed = window.confirm(`Are you sure you want to add ${formatAmount(entryAmount)} manual payment?`);
          if (!confirmed) {
            return;
          }
          setModalIsLoading(true);
          const payload = {
            RecordLoanStatementManualPayment: {
              amountCredited: minorCurrencyUnits(entryAmount),
            },
          };
          await administerLoanStatement(apiAprilBaseUri, loanStatementId, payload);
          setLoanStatementId('');
          setNewEntryAmount('');
          setShowModal(false);
        }
      } catch (e) {
        setModalErrorMsg(getErrorMessage(e));
      } finally {
        setModalIsLoading(false);
      }
    },
    [apiAprilBaseUri, loanStatementId, newEntryAmount, setShowModal],
  );

  const handleCloseModalMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setModalErrorMsg('');
  };

  const handleCancel = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(false);
    setModalErrorMsg('');
    setLoanStatementId('');
    setNewEntryAmount('');
  };

  return (
    <>
      {showModal && (
        <Modal
          title="Manual Payment"
          cancelBtnText="Cancel"
          confirmBtnText="Add"
          handleCancel={handleCancel}
          handleSubmit={handleAddEntry}
          disableConfirmBtn={modalIsLoading}
          isLoading={modalIsLoading}
          testId={'loan-statement-manual-payment-modal'}
        >
          {modalErrorMsg.length > 0 && (
            <Message
              className="mt-2 mb-0"
              success={false}
              description={modalErrorMsg}
              handleClose={handleCloseModalMsg}
            />
          )}
          <div className="form">
            <FormRow>
              <Label>Loan Statement ID</Label>
              <FormField>
                <TextInput
                  type="text"
                  name="loanStatementId"
                  placeholder="Eg: lstmt_xxxxxxxxxxxxxxxx"
                  value={loanStatementId}
                  required
                  onChange={(e) => setLoanStatementId(e.target.value)}
                  data-testid="loan-statement-manual-payment-modal-loanstatementid-input"
                />
              </FormField>
            </FormRow>
            <FormRow>
              <Label>Amount Credited</Label>
              <FormField>
                <TextInput
                  type="text"
                  name="newEntryAmount"
                  placeholder="Eg: $1,000.00"
                  value={newEntryAmount}
                  required
                  onChange={(e) => setNewEntryAmount(e.target.value)}
                  data-testid="loan-statement-manual-payment-modal-amount-input"
                />
              </FormField>
            </FormRow>
          </div>
        </Modal>
      )}
    </>
  );
};

export default LoanStatementManualPayment;
